import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { deltaPct, fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, alert, card, cardHead, muted, td, tdR, th, thR } from './Overview';

// "Zmiany i operatorzy" (spec 7.6): shift cards with comparison and the "level up to the
// best shift = +X kg" conclusion, then the operator table.

interface ShiftRow extends Kpi { shift: string; previous: Kpi | null }
interface Operator extends Kpi { id: number; name: string; lines: string[] }
interface Data { shifts: ShiftRow[]; levelUpKg: number | null; bestKgPerH: number | null; operators: Operator[] }

const Shifts: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { shift, ...rest } = toQuery(filters);
    api.get('/analytics/shifts', { params: rest })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const Delta: React.FC<{ cur: number | null; prev: number | null | undefined; invert?: boolean }> = ({ cur, prev, invert }) => {
    const dlt = filters.compare ? deltaPct(cur, prev) : null;
    if (dlt === null) return null;
    const good = invert ? dlt < 0 : dlt > 0;
    return <span style={{ color: good ? '#059669' : '#dc2626', fontWeight: 700, fontSize: 12, marginLeft: 6 }}>{dlt >= 0 ? '▲' : '▼'} {Math.abs(dlt * 100).toFixed(1)}%</span>;
  };

  const best = data?.shifts.reduce<ShiftRow | null>((b, s) => (s.kgPerH !== null && (b === null || (b.kgPerH ?? 0) < s.kgPerH) ? s : b), null);

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>👥 {t('shifts.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('shifts.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}

      {data && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
            {data.shifts.map(s => (
              <div key={s.shift} style={{ ...card, marginBottom: 0, borderColor: best?.shift === s.shift ? '#667eea' : '#e2e8f0' }}>
                <div style={cardHead}>
                  <b>{t(s.shift === 'zmiana_1' ? 'analytics.shift1' : 'analytics.shift2')}</b>
                  {best?.shift === s.shift && <span style={{ fontSize: 11, fontWeight: 700, color: '#667eea', background: '#eef2ff', padding: '2px 8px', borderRadius: 10 }}>{t('shifts.best')}</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    [t('analytics.kpiNetKg'), `${fmtNum(s.kgNet, 1)} kg`, s.kgNet, s.previous?.kgNet, false],
                    [t('analytics.kpiSpools'), fmtNum(s.pcs), s.pcs, s.previous?.pcs, false],
                    [t('analytics.colProd'), fmtMin(s.prodMin), s.prodMin, s.previous?.prodMin, false],
                    ['kg/h', fmtNum(s.kgPerH, 1), s.kgPerH, s.previous?.kgPerH, false],
                    ['Availability', fmtPct(s.availability), s.availability, s.previous?.availability, false],
                    [t('analytics.colSpeed'), fmtNum(s.avgSpeed, 1), s.avgSpeed, s.previous?.avgSpeed, false],
                    ['Performance', fmtPct(s.performance), s.performance, s.previous?.performance, false],
                    [t('analytics.kpiShortStops'), `${s.ssCnt} · ${fmtNum(s.stopsPerProdH, 2)}/h`, s.ssCnt, s.previous?.ssCnt, true],
                    [t('analytics.kpiDowntime'), fmtMin(s.downMin), s.downMin, s.previous?.downMin, true],
                    [t('analytics.kpiFlowKg'), `${fmtNum(s.kgFlow, 1)} kg`, s.kgFlow, s.previous?.kgFlow, false],
                  ].map(([label, value, cur, prev, invert]) => (
                    <div key={String(label)}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>{String(label)}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>{String(value)}<Delta cur={cur as number | null} prev={prev as number | null | undefined} invert={invert as boolean} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ ...card, marginBottom: 0, background: '#f8fafc' }}>
              <div style={cardHead}><b>{t('shifts.conclusion')}</b></div>
              {data.levelUpKg !== null && best ? (
                <>
                  <div style={{ fontSize: 30, fontWeight: 800, color: '#059669' }}>+{fmtNum(data.levelUpKg, 0)} kg</div>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>{t('shifts.conclusionText', { best: t(best.shift === 'zmiana_1' ? 'analytics.shift1' : 'analytics.shift2'), rate: fmtNum(data.bestKgPerH, 1) })}</div>
                </>
              ) : <div style={muted}>{t('shifts.noConclusion')}</div>}
            </div>
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('shifts.operators')}</b><span style={muted}>{t('shifts.operatorsSub')}</span></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={th}>{t('shifts.colOperator')}</th><th style={th}>{t('shifts.colLines')}</th><th style={thR}>{t('analytics.colProd')}</th><th style={thR}>{t('analytics.colPcs')}</th><th style={thR}>{t('analytics.colKg')}</th><th style={thR}>kg/h</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>{t('shifts.colStops')}</th><th style={thR}>{t('analytics.colDown')}</th><th style={thR}>{t('shifts.colDownPct')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.operators.length === 0 ? <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>—</td></tr> : data.operators.map(o => (
                    <tr key={o.id}>
                      <td style={{ ...td, fontWeight: 700 }}>{o.name}</td>
                      <td style={{ ...td, color: '#64748b' }}>{o.lines.join(', ')}</td>
                      <td style={tdR}>{o.hasTelemetry ? fmtMin(o.prodMin) : '—'}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(o.pcs)}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(o.kgNet, 1)}</td>
                      <td style={tdR}>{fmtNum(o.kgPerH, 1)}</td>
                      <td style={tdR}>{fmtNum(o.avgSpeed, 1)}</td>
                      <td style={tdR}>{o.hasTelemetry ? `${o.ssCnt} · ${fmtNum(o.stopsPerProdH, 2)}/h` : '—'}</td>
                      <td style={tdR}>{o.hasTelemetry ? fmtMin(o.downMin) : '—'}</td>
                      <td style={tdR}>{o.availability === null ? '—' : fmtPct(1 - o.availability)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
